import React from 'react';
import { Link } from 'react-router-dom';
import { Video, BookOpen, FileText, HelpCircle, ArrowRight, ArrowLeft, ExternalLink } from 'lucide-react';
import SEO from '../components/SEO';

const CIRECorporateAccess = () => {
  const accessTiles = [
    {
      id: 'video-course',
      title: 'Member Login',
      subtitle: 'Video Course Access',
      description: 'Access your CIRE video tutorials, question bank, and mock exams on Kajabi.',
      icon: Video,
      color: '#1e40af',
      lightColor: '#dbeafe',
      link: 'https://vittafinance.mykajabi.com/login',
      external: true
    },
    {
      id: 'interactive-book',
      title: 'CIRE Interactive Book',
      subtitle: 'Quick Study Guide',
      description: 'Access your interactive study guide with flashcards, quizzes, and games.',
      icon: BookOpen,
      color: '#7c3aed',
      lightColor: '#ede9fe',
      link: '/books/cire-interactive',
      external: false
    },
    {
      id: 'mock-exams',
      title: 'CIRE Mock Exams',
      subtitle: '5 Full Practice Tests',
      description: 'Access 5 complete mock exams with 550 practice questions.',
      icon: FileText,
      color: '#dc2626',
      lightColor: '#fee2e2',
      link: '/books/cire-mock-exam-pack',
      external: false
    },
    {
      id: 'question-bank',
      title: 'CIRE Question Bank',
      subtitle: 'Element-Wise Practice',
      description: 'Access 900+ practice questions organized by element.',
      icon: HelpCircle,
      color: '#16a34a',
      lightColor: '#dcfce7',
      link: 'https://vittafinance.ca/books/cire-element-bank',
      external: true
    }
  ];

  return (
    <>
      <SEO 
        title="CIRE Corporate Access | Vitta Finance Academy"
        description="Access your CIRE corporate study materials including video course, interactive book, mock exams, and question bank."
      />

      {/* Header */}
      <section style={{
        background: 'linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%)',
        padding: '60px 20px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.15)',
            padding: '6px 16px',
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600',
            marginBottom: '16px'
          }}>
            CORPORATE ACCESS
          </div>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: '700',
            marginBottom: '12px',
            fontFamily: 'Georgia, serif'
          }}>
            CIRE Study Materials
          </h1>
          <p style={{
            fontSize: '1.1rem',
            opacity: '0.9',
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            Canadian Investment Regulatory Exam
          </p>
        </div>
      </section>

      {/* Access Tiles */}
      <section style={{ padding: '60px 20px', background: '#f8fafc' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px'
          }}>
            {accessTiles.map((tile) => {
              const IconComponent = tile.icon;
              const TileWrapper = tile.external ? 'a' : Link;
              const tileProps = tile.external 
                ? { href: tile.link, target: '_blank', rel: 'noopener noreferrer' }
                : { to: tile.link, target: '_blank' };

              return (
                <TileWrapper
                  key={tile.id}
                  {...tileProps}
                  data-testid={`cire-${tile.id}-tile`}
                  style={{
                    display: 'block',
                    background: 'white',
                    borderRadius: '16px',
                    padding: '32px',
                    border: '2px solid #e2e8f0',
                    textDecoration: 'none',
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-6px)';
                    e.currentTarget.style.borderColor = tile.color;
                    e.currentTarget.style.boxShadow = `0 12px 40px ${tile.color}20`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    width: '60px',
                    height: '60px',
                    background: tile.lightColor,
                    borderRadius: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '20px'
                  }}>
                    <IconComponent size={28} color={tile.color} />
                  </div>
                  <h3 style={{
                    fontSize: '1.25rem',
                    fontWeight: '700',
                    color: '#0f172a',
                    marginBottom: '4px'
                  }}>
                    {tile.title}
                  </h3>
                  <p style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: tile.color,
                    marginBottom: '12px'
                  }}>
                    {tile.subtitle}
                  </p>
                  <p style={{
                    fontSize: '0.9rem',
                    color: '#64748b',
                    lineHeight: '1.5',
                    marginBottom: '20px'
                  }}>
                    {tile.description}
                  </p>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: tile.color,
                    fontWeight: '600',
                    fontSize: '0.95rem'
                  }}>
                    Access Now
                    {tile.external ? <ExternalLink size={16} /> : <ArrowRight size={16} />}
                  </div>
                </TileWrapper>
              );
            })}
          </div>

          {/* Back Link */}
          <div style={{ textAlign: 'center', marginTop: '40px' }}>
            <Link
              to="/corporate-discounts"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#64748b',
                fontSize: '14px',
                fontWeight: '500',
                textDecoration: 'none'
              }}
            >
              <ArrowLeft size={16} />
              Back to Corporate Pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default CIRECorporateAccess;
